import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Receipt, ArrowRight, Truck, Download } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { formatPrice } from "@/lib/storefront";

export const Route = createFileRoute("/_authenticated/_account/orders")({
  head: () => ({
    meta: [
      { title: "Orders — CoderLity" },
      { name: "description", content: "View your order history and receipts." },
    ],
  }),
  component: OrdersPage,
});

type OrderRow = {
  id: string;
  status: string;
  currency: string;
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
  created_at: string;
  paid_at: string | null;
  order_items: { id: string; product_name: string; variant_name: string; qty: number; unit_price_cents: number }[];
  order_tracking_events: { status: string; occurred_at: string }[];
};

const TRACKING_LABEL: Record<string, string> = {
  placed: "Order placed",
  confirmed: "Confirmed",
  processing: "Processing",
  ready_for_shipment: "Ready to ship",
  shipped: "Shipped",
  in_transit: "In transit",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  delayed: "Delayed",
  exception: "Exception",
  cancelled: "Cancelled",
  returned: "Returned",
};

const statusVariant: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  paid: "default",
  pending: "secondary",
  refunded: "outline",
  cancelled: "destructive",
  failed: "destructive",
};

function OrdersPage() {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    enabled: !!user,
    queryKey: ["my-orders", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("id, status, currency, subtotal_cents, discount_cents, total_cents, created_at, paid_at, order_items(id, product_name, variant_name, qty, unit_price_cents), order_tracking_events(status, occurred_at)")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as OrderRow[];
    },
  });

  return (
    <>
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Order history</h1>
        <p className="text-sm text-muted-foreground">All purchases tied to your account.</p>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Loading orders…</CardContent></Card>
      ) : !data || data.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No orders yet</CardTitle>
            <CardDescription>Once you complete a purchase it'll show up here.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild><Link to="/software">Browse the store</Link></Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data.map((o) => (
            <Card key={o.id}>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Receipt className="h-4 w-4 text-primary" />
                      Order #{o.id.slice(0, 8).toUpperCase()}
                    </CardTitle>
                    <CardDescription>
                      {new Date(o.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })} ·{" "}
                      {o.order_items.length} item{o.order_items.length === 1 ? "" : "s"}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={statusVariant[o.status] ?? "secondary"} className="capitalize">{o.status}</Badge>
                    <div className="text-right">
                      <div className="text-xs text-muted-foreground">Total</div>
                      <div className="font-semibold">{formatPrice(o.total_cents, o.currency)}</div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const latest = [...(o.order_tracking_events ?? [])].sort(
                    (a, b) => +new Date(b.occurred_at) - +new Date(a.occurred_at)
                  )[0];
                  if (!latest) return null;
                  return (
                    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                      <Truck className="h-4 w-4 text-primary" />
                      <span className="font-medium">{TRACKING_LABEL[latest.status] ?? latest.status}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {new Date(latest.occurred_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                    </div>
                  );
                })()}
                <ul className="divide-y divide-border text-sm">
                  {o.order_items.map((i) => (
                    <li key={i.id} className="flex items-center justify-between py-2">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{i.product_name}</div>
                        <div className="text-xs text-muted-foreground">{i.variant_name} · Qty {i.qty}</div>
                      </div>
                      <div className="text-sm font-medium">{formatPrice(i.unit_price_cents * i.qty, o.currency)}</div>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap justify-end gap-2">
                  {(o.status === "paid" || o.status === "fulfilled" || o.status === "refunded") && (
                    <Button asChild variant="outline" size="sm" className="gap-2">
                      <Link to="/receipt/$orderId" params={{ orderId: o.id }} target="_blank">
                        <Download className="h-3.5 w-3.5" /> Download receipt
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm" className="gap-2">
                    <Link to="/orders/$orderId" params={{ orderId: o.id }}>
                      Track order <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
